import URL from 'src/api/url'

/**
 * Renders the optional attributes payload for a markup snippet.
 *
 * Single-quoted on purpose: the value is JSON, which is full of double quotes. Emitted only when
 * there is something to emit, so the common snippet stays as short as it is today.
 */
const attributesAttr = config =>
  config?.attributes && Object.keys(config.attributes).length > 0
    ? `\n    data-mcg-attributes='${JSON.stringify(config.attributes)}'`
    : ''

export const WIDGET_JS_TEMPLATE = (
  config = {},
  collectionData = {},
  userData = {},
) => {
  const collectionId = collectionData?.id
  // widget-redirect only accepts the key of the shop that owns the collection. A collaborator from
  // another shop has a different key of their own, so the owner's wins when the collection has it.
  const publicKey = collectionData?.publicKey ?? userData?.publicKey
  const userName = userData?.email
  const name = userData?.name != null ? userData?.name : ''
  const surname = userData?.surname != null ? userData?.surname : ''
  const userNameSurname = name + ' ' + surname
  const sourceUrl = URL.APP_URL

  const selector = config?.selector || 'embed-widget'
  return selector === 'embed-widget'
    ? `<script>
     window.mcgWidget = (window.mcgWidget || { 
      queue: [], 
      on: function(n, x) { window.mcgWidget.queue.push([n, x]); }, 
      init: function(x) { window.mcgWidget.queue.push(x); }
    })
window.mcgWidget.init({
 selector: "#embed-widget",
 widget: "embed",
 collection: "${collectionId}",
 title: "${collectionData?.name || 'Embedded Content'}",
 publicKey: "${publicKey}",
 nameSurname: "${userNameSurname || ''}", // optional
 userName: "${userName}", // optional
 // attributes: { "plan": "premium", "role": "admin" }, // optional, appears in your reports
});
</script>
<script async src="${sourceUrl}widget/mcg-widget-loader.min.js"></script>
`
    : `<script async src="${sourceUrl}widget/mcg-widget-loader.min.js"></script>`
}

export const WIDGET_HTML_TEMPLATES = {
  sidebar: config => `<a id="${(config?.selector || '#sidebar-widget').replace(
    '#',
    '',
  )}" 
    href="#"
    class="widget-button mcg-widget" 
    data-mcg-widget="${config?.widget || 'sidebar'}"
    data-mcg-collection="${config?.collection || ''}"
    data-mcg-title="${config?.title || 'Help Center'}"
    data-mcg-position="${config?.position || 'left'}"
    data-mcg-width="${config?.width + 'px' || '450px'}"
    data-mcg-theme="${config?.theme || 'light'}"
    data-mcg-public-key="${config?.publicKey || ''}"
    data-mcg-user-name="${config?.userName || ''}" 
    data-mcg-name-surname="${config?.nameSurname || ''}"${attributesAttr(config)}
    style="border-radius: 12px;
          background: #002E47;
          border: 1px solid #002E47; 
          display: inline-flex;
          padding: 10px 20px;
          color: #FFFFFF;
          text-align: center;
          font-family: Inter;
          font-size: 14px;
          font-weight: 500;
          line-height: normal;
          text-decoration: none"
    >
    Academy
</a>
  <!-- data-mcg-user-name, data-mcg-name-surname & data-mcg-attributes are optional -->
`,
  modal: config => `<a id="${(config?.selector || '#modal-widget').replace(
    '#',
    '',
  )}" 
    href="#"
    class="widget-button mcg-widget" 
    data-mcg-widget="${config?.widget || 'modal'}"
    data-mcg-collection="${config?.collection || ''}"
    data-mcg-title="${config?.title || 'Documentation'}"
    data-mcg-modal-max-width="${config?.modalMaxWidth + 'px' || '800'}"
    data-mcg-theme="${config?.theme || 'light'}"
    data-mcg-public-key="${config?.publicKey || ''}"
    data-mcg-user-name="${config?.userName || ''}" 
    data-mcg-name-surname="${config?.nameSurname || ''}"${attributesAttr(config)} 
    >
    Academy
</a>
   <!-- data-mcg-user-name, data-mcg-name-surname & data-mcg-attributes are optional -->
`,
  direct: config => `<a id="${(config?.selector || '#direct-widget').replace(
    '#',
    '',
  )}" 
    href="#"
    class="widget-button mcg-widget" 
    data-mcg-direct-link-target="${config?.directLinkTarget || '_blank'}"
    data-mcg-widget="${config?.widget || 'direct'}"
    data-mcg-collection="${config?.collection || ''}"
    data-mcg-public-key="${config?.publicKey || ''}"
    data-mcg-user-name="${config?.userName || ''}" 
    data-mcg-name-surname="${config?.nameSurname || ''}"${attributesAttr(config)} 
    >
    Academy
</a>
  <!-- data-mcg-user-name, data-mcg-name-surname & data-mcg-attributes are optional -->
`,
  embed: config =>
    `<div id="embed-widget" class="widget-embed" style="width: 100%;"> </div>`,
}

export const DEFAULT_WIDGET_CONFIGS = (collectionData = {}) => {
  const collectionId = collectionData?.id
  const collectionTitle = collectionData?.name
  return {
    sidebar: {
      selector: '#sidebar-widget',
      widget: 'sidebar',
      collection: collectionId,
      title: collectionTitle || 'Help Center',
      position: 'right',
      width: '450',
      theme: 'light',
      zIndex: 9999,
      closeOnClickOutside: false,
    },
    modal: {
      selector: '#modal-widget',
      widget: 'modal',
      collection: collectionId,
      title: collectionTitle || 'Documentation',
      modalMaxWidth: '1280',
      theme: 'light',
      zIndex: 9999,
      closeOnClickOutside: true,
    },
    direct: {
      selector: '#direct-widget',
      widget: 'direct',
      collection: collectionId,
      directLinkTarget: '_blank',
      directLinkText: 'Open Academy',
    },
    embed: {
      selector: 'embed-widget',
      widget: 'embed',
      collection: collectionId,
    },
  }
}
